/**
 * ContractorsList Component
 *
 * Admin table of all contractors with search, pagination, and skill badges.
 */

import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import type { ContractorProfile, BillingType } from '@/types/contractors';
import { contractorsApi } from '@/services/contractorsApi';

const PAGE_SIZE = 20;

const BILLING_TYPE_COLORS: Record<BillingType, string> = {
  ett: 'bg-blue-100 text-blue-700',
  stt: 'bg-purple-100 text-purple-700',
  fixed: 'bg-green-100 text-green-700',
  company_set: 'bg-orange-100 text-orange-700',
};

export function ContractorsList(): React.JSX.Element {
  const { t } = useTranslation('contractors');
  const navigate = useNavigate();

  const [contractors, setContractors] = useState<ContractorProfile[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);


  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  // Fetch contractors
  const fetchContractors = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await contractorsApi.list({
        search: debouncedSearch || undefined,
        limit: PAGE_SIZE,
        offset: (page - 1) * PAGE_SIZE,
      });
      setContractors(result);
      setTotal(result.length);
    } catch (err) {
      setError(t('errors.loadFailed'));
      console.error('Failed to load contractors:', err);
    } finally {
      setIsLoading(false);
    }
  }, [debouncedSearch, page, t]);

  useEffect(() => {
    void fetchContractors();
  }, [fetchContractors]);

  // Reset page when search changes
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch]);

  const totalPages = Math.ceil(total / PAGE_SIZE);

  const formatCurrency = (amount: number | null, currency: string = 'EUR'): string => {
    if (amount === null || amount === undefined) return '-';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(amount);
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-900">{t('contractors.title')}</h2>
        <button
          onClick={() => navigate('/contractors/new')}
          className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
        >
          {t('contractors.add')}
        </button>
      </div>

      {/* Search */}
      <div className="relative">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t('contractors.searchPlaceholder')}
          className="w-full px-4 py-2 pl-10 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        />
        <svg className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
      </div>

      {/* Error */}
      {error && (
        <div className="p-3 text-sm text-red-700 bg-red-50 rounded-lg">{error}</div>
      )}

      {/* Table */}
      <div className="overflow-x-auto bg-white rounded-lg border border-gray-200">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('contractors.name')}</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('contractors.company')}</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('contractors.skills')}</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('contractors.billingTypes')}</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('contractors.hourlyRate')}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {isLoading ? (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-sm text-gray-500">
                  {t('common.loading')}
                </td>
              </tr>
            ) : contractors.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-sm text-gray-500">
                  {t('contractors.noResults')}
                </td>
              </tr>
            ) : (
              contractors.map((contractor) => (
                <tr
                  key={contractor.id}
                  onClick={() => navigate(`/contractors/${contractor.id}`)}
                  className="hover:bg-gray-50 cursor-pointer transition-colors"
                >
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">
                    {contractor.display_name}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {contractor.company_name || '-'}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      {contractor.skills.slice(0, 3).map((skill) => (
                        <span key={skill} className="inline-flex px-2 py-0.5 text-xs font-medium bg-indigo-100 text-indigo-700 rounded-full">
                          {skill}
                        </span>
                      ))}
                      {contractor.skills.length > 3 && (
                        <span className="inline-flex px-2 py-0.5 text-xs text-gray-500">
                          +{contractor.skills.length - 3}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      {contractor.billing_types.map((bt) => (
                        <span key={bt} className={`inline-flex px-2 py-0.5 text-xs font-medium rounded ${BILLING_TYPE_COLORS[bt]}`}>
                          {bt.toUpperCase()}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900">
                    {formatCurrency(contractor.hourly_rate)}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-600">
            {t('common.showing', { from: (page - 1) * PAGE_SIZE + 1, to: Math.min(page * PAGE_SIZE, total), total })}
          </span>
          <div className="flex gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              {t('common.previous')}
            </button>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              {t('common.next')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
